import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const STAGES = [
  {
    title: "IDEA",
    accent: "#FF8800",
    detail:
      "Every great business begins with an idea — but not every idea is a business. LaunchLab identifies real opportunities, unmet problems and market gaps, then stress-tests the raw concept against market size, timing, competition and founder fit before a single rupee is invested.",
  },
  {
    title: "VALIDATION",
    accent: "#00B0FF",
    detail:
      "We replace assumptions with evidence. LaunchLab studies real customers, competitors, demand signals and willingness to pay through surveys, landing-page tests, pilot offers and data — so the market, not opinions, decides whether the idea deserves investment.",
  },
  {
    title: "BUSINESS MODEL",
    accent: "#00E676",
    detail:
      "Revenue streams, pricing strategy, unit economics, cost structures and scalability are engineered into a practical model. The result is a business architecture designed to survive contact with reality — and to grow profitably once it does.",
  },
  {
    title: "BRANDING",
    accent: "#FF8800",
    detail:
      "Name, logo, visual identity, positioning, packaging and communication — LaunchLab builds a complete brand system that makes the business memorable, trusted and premium from day one, across every customer touchpoint.",
  },
  {
    title: "TECHNOLOGY",
    accent: "#00B0FF",
    detail:
      "The digital backbone of the venture: websites, e-commerce platforms, mobile applications, business software, automation systems and AI-enabled solutions — built to scale with the business instead of holding it back.",
  },
  {
    title: "OPERATIONS",
    accent: "#00E676",
    detail:
      "Procurement, inventory, logistics, customer management, vendors, employees and branch operations — LaunchLab designs practical operating systems that keep the business running smoothly every single day, not just on launch day.",
  },
  {
    title: "MARKETING",
    accent: "#FF8800",
    detail:
      "Social media, performance marketing, content, influencer campaigns and customer-acquisition engines — a complete digital growth system that creates demand consistently rather than occasionally.",
  },
  {
    title: "LAUNCH",
    accent: "#00B0FF",
    detail:
      "Launch is choreography, not luck. LaunchLab coordinates brand, product, technology, operations and marketing so every component goes live together — taking the business from concept to market with maximum impact.",
  },
  {
    title: "GROWTH",
    accent: "#00E676",
    detail:
      "Post-launch, the real work begins. LaunchLab helps improve revenue, customers, operations, technology and distribution — turning a launched business into a scaling one, and a scaling one into an institution.",
  },
];

export function Pipeline() {
  const [open, setOpen] = useState(null);

  return (
    <section id="about" data-testid="about-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:sticky lg:top-28"
          >
            <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">01 — Who We Are</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="about-headline">
              WHAT IS <span className="text-gradient-saffron">LAUNCHLAB?</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              LaunchLab is a startup and business creation platform designed to help
              entrepreneurs convert ideas into scalable businesses.
            </p>
            <p className="mt-4 text-base text-slate-400 leading-relaxed">
              LaunchLab brings together every capability required to build a business —
              strategy, brand, technology, operations, marketing and growth — under one
              ecosystem, so founders never have to assemble the pieces alone.
            </p>
            <p className="mt-6 font-mono-tech text-[11px] tracking-[0.25em] text-slate-500 uppercase">
              Tap any stage to see what happens inside it ↓
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {["Startup Creation", "Business Development", "Innovation Platform"].map((t) => (
                <span key={t} className="glass px-4 py-2 rounded-full text-xs font-mono-tech tracking-wider text-slate-300">
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          <div className="relative" data-testid="pipeline-flow">
            <div className="absolute left-[27px] top-4 bottom-4 w-px bg-gradient-to-b from-[#FF8800] via-[#00B0FF] to-[#00E676] opacity-40" />
            <div className="space-y-3">
              {STAGES.map((stage, i) => {
                const isOpen = open === i;
                return (
                  <motion.div
                    key={stage.title}
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="relative"
                    data-testid={`pipeline-stage-${stage.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      data-testid={`pipeline-toggle-${stage.title.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-expanded={isOpen}
                      className="w-full flex items-center gap-4 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800] rounded-xl"
                    >
                      <div
                        className="w-14 h-14 rounded-xl glass flex items-center justify-center font-mono-tech text-sm font-semibold shrink-0 z-10 transition-transform duration-300 group-hover:scale-105"
                        style={{ color: stage.accent, boxShadow: `0 0 20px ${stage.accent}22` }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div
                        className={`glass flex-1 rounded-xl px-5 py-3.5 flex items-center justify-between transition-colors duration-300 ${
                          isOpen ? "border-white/25" : "group-hover:border-white/20"
                        }`}
                        style={isOpen ? { boxShadow: `0 0 26px ${stage.accent}1e` } : undefined}
                      >
                        <span className="font-display font-semibold tracking-[0.12em] text-white text-sm sm:text-base">
                          {stage.title}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 transition-all duration-300 ${isOpen ? "rotate-180" : ""}`}
                          style={{ color: isOpen ? stage.accent : "#475569" }}
                        />
                      </div>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div
                            className="ml-[72px] mt-2 rounded-xl border border-white/10 bg-[#0D111D]/80 p-5"
                            data-testid={`pipeline-detail-${stage.title.toLowerCase().replace(/\s+/g, "-")}`}
                            style={{ borderLeft: `2px solid ${stage.accent}` }}
                          >
                            <p className="text-sm text-slate-300 leading-relaxed">{stage.detail}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
